import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowUpRight, Dumbbell, HeartPulse, Instagram, MoveUpRight, Sparkles, Timer, Zap } from 'lucide-react';
import { Button, Section, Heading, Badge, InstagramWidget } from '../components/UI.jsx';
import { images, metadata, team } from '../data.js';
import { getOptimizedImageUrl } from '../lib/utils.js';

const benefitCards = [
  {
    icon: Zap,
    eyebrow: '01',
    title: 'EMS med kant',
    text: 'Elektrisk Muskel Stimulation aktiverer de store muskelgrupper intensivt, mens du træner kontrollerede øvelser med personlig instruktion.',
    link: '/om-ems-traening'
  },
  {
    icon: Timer,
    eyebrow: '02',
    title: '20 minutter',
    text: 'Et format skabt til en travl hverdag: kort, effektivt og målrettet — uden at gå på kompromis med kvaliteten i din træning.',
    link: '/priser'
  },
  {
    icon: HeartPulse,
    eyebrow: '03',
    title: 'Faglig ro',
    text: 'Vi kombinerer træning med fysioterapeutisk forståelse, så kroppen mødes præcist, sikkert og med respekt for dit niveau.',
    link: '/fysioterapi'
  }
];

const Home = () => {
  const [loadVideo, setLoadVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let triggered = false;
    let timeoutId = null;

    const triggerVideoLoad = () => {
      if (triggered) return;
      triggered = true;
      setLoadVideo(true);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('scroll', triggerVideoLoad);
      window.removeEventListener('touchstart', triggerVideoLoad);
      window.removeEventListener('click', triggerVideoLoad);
      if (timeoutId) clearTimeout(timeoutId);
    };

    // Listen for real user interactions (scroll, touch, click) for immediate load
    window.addEventListener('scroll', triggerVideoLoad, { passive: true });
    window.addEventListener('touchstart', triggerVideoLoad, { passive: true });
    window.addEventListener('click', triggerVideoLoad, { passive: true });

    // Automatically load after 1.5 seconds if no interaction has happened yet
    timeoutId = setTimeout(() => {
      triggerVideoLoad();
    }, 1500);

    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (loadVideo && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(err => {
        console.log("Autoplay prevented or video load failed:", err);
      });
    }
  }, [loadVideo]);

  return (
    <div className="bg-[#F1E5DC] selection:bg-[#A8814E] selection:text-white">
      <Helmet>
        <title>EMS Træning &amp; Fysioterapi Frederiksberg | LK STUDIO</title>
        <meta name="description" content="Få maksimal effekt på kun 20 minutter med personlig EMS træning og professionel fysioterapi hos LK STUDIO på Frederiksberg. Book en prøvetime i dag!" />
        <link rel="canonical" href="https://lkstudioems.com/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lkstudioems.com/" />
        <meta property="og:title" content="EMS Træning &amp; Fysioterapi Frederiksberg | LK STUDIO" />
        <meta property="og:description" content="Få maksimal effekt på kun 20 minutter med personlig EMS træning og professionel fysioterapi hos LK STUDIO på Frederiksberg. Book en prøvetime i dag!" />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/acf24aad-5bd9-4c13-ac2b-a8f96ec93620.jpg" />
        <meta property="og:site_name" content="LK STUDIO" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://lkstudioems.com/" />
        <meta name="twitter:title" content="EMS Træning &amp; Fysioterapi Frederiksberg | LK STUDIO" />
        <meta name="twitter:description" content="Få maksimal effekt på kun 20 minutter med personlig EMS træning og professionel fysioterapi hos LK STUDIO på Frederiksberg. Book en prøvetime i dag!" />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/acf24aad-5bd9-4c13-ac2b-a8f96ec93620.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "HealthAndBeautyBusiness",
              "name": "LK STUDIO",
              "image": "https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/acf24aad-5bd9-4c13-ac2b-a8f96ec93620.jpg",
              "@id": "https://lkstudioems.com/#business",
              "url": "https://lkstudioems.com/",
              "telephone": "+4528852282",
              "priceRange": "$$",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Engtoftevej 8",
                "addressLocality": "Frederiksberg C",
                "postalCode": "1816",
                "addressCountry": "DK"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 55.6738,
                "longitude": 12.5369
              },
              "sameAs": [
                "https://www.instagram.com/lkstudio_cph/"
              ]
            }
          `}
        </script>
      </Helmet>
      <section className="relative min-h-screen overflow-hidden bg-[#F1E5DC] pt-32 pb-20 lg:pt-44 lg:pb-32">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            poster={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/shutterstock-1090740605-poster.jpg", { width: 1600, quality: 70 })}
            className="h-full w-full object-cover opacity-15 grayscale-[0.5]"
          >
            {loadVideo && (
              <source src="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/shutterstock-1090740605.mp4" type="video/mp4" />
            )}
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#F1E5DC]/80 via-transparent to-[#F1E5DC]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
              <Badge className="mb-8 border-[#1C1B1A]/10 bg-white/50 text-[#1C1B1A] backdrop-blur-md">
                <span className="h-px w-5 bg-[#1C1B1A]/20" />
                fysioterapi - ems - frederiksberg
              </Badge>
              <h1 className="font-serif text-5xl font-normal leading-[0.9] tracking-[-0.05em] text-[#1C1B1A] sm:text-7xl lg:text-[7.5rem]">
                Styrke i et <br />
                <span className="text-[#A8814E] italic">roligere</span> tempo.
              </h1>
              
              <div className="mt-12 grid gap-10 border-t border-[#1C1B1A]/10 pt-12 pb-12 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="max-w-2xl">
                  <p className="text-xl leading-relaxed text-[#494540] sm:text-2xl">
                    Personlig EMS træning og fysioterapeutisk behandling i et privat studie, hvor effektivitet, teknik og nærvær mødes.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <Button
                      variant="primary"
                      onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}
                    >
                      Book prøvetime
                      <ArrowUpRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="border-[#1C1B1A] text-[#1C1B1A] hover:bg-[#1C1B1A] hover:text-white"
                      onClick={() => window.location.href = '/om-ems-traening'}
                    >
                      Se metoden
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-end justify-start gap-16 lg:justify-end">
                  <div className="flex flex-col items-start gap-4 lg:items-end">
                    <div className="flex -space-x-3">
                      {team.map((member, i) => (
                        <img 
                          key={i}
                          src={getOptimizedImageUrl(member.image, { width: 112, height: 112, fit: 'cover' })} 
                          className="h-14 w-14 rounded-full border-2 border-[#F1E5DC] object-cover" 
                          alt={member.name} 
                        />
                      ))}
                    </div>
                    <div className="text-left lg:text-right">
                      <p className="text-lg font-bold text-[#1C1B1A] sm:text-xl">Tilmeld dig 200+ glade medlemmer</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
        </div>
      </section>

      <Section className="bg-[#F1E5DC]">
        <div className="mx-auto max-w-7xl">
          <div className="mb-14 grid gap-8 md:grid-cols-[0.9fr_1.1fr] md:items-end">
            <div>
              <Badge>En anden træningsoplevelse</Badge>
              <h2 className="mt-6 font-serif text-4xl leading-[0.95] tracking-[-0.05em] text-[#1C1B1A] sm:text-6xl lg:text-7xl">
                Minimal støj. Maksimal effekt.
              </h2>
            </div>
            <p className="max-w-2xl text-lg leading-8 text-[#494540] md:ml-auto">
              Viben er rolig, men træningen er stadig konkret, teknisk og resultatorienteret. Som fysioterapeut skræddersyr jeg et træningsprogram, der især er tilpasset dine mål og udfordringer, så vi sammen når dine mål optimalt.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {benefitCards.map((card, index) => (
              <motion.article
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ delay: index * 0.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="group flex flex-col border-t border-[#CEBDAA] pt-8 transition-all duration-500"
              >
                <div className="mb-8 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9A8E81]">{card.eyebrow}</span>
                  <card.icon className="h-5 w-5 text-[#CEBDAA] transition-colors group-hover:text-[#A8814E]" />
                </div>
                <h3 className="font-serif text-2xl tracking-tight text-[#1C1B1A] sm:text-3xl">{card.title}</h3>
                <p className="mt-5 text-base leading-relaxed text-[#494540]">{card.text}</p>
                <a href={card.link} className="mt-8 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#1C1B1A]">
                  Læs mere
                  <MoveUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </Section>

      <Section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div className="relative order-2 lg:order-1">
            <div className="aspect-[4/5] overflow-hidden rounded-[3rem] bg-[#F1E5DC]">
              <img 
                src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/3743e83c-e5e7-4b97-a837-296ec7a22b22.jpg", { width: 800, quality: 80 })} 
                className="h-full w-full object-cover grayscale-[0.2]" 
                alt="EMS træning i LK STUDIO" 
                loading="lazy"
              />
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <Heading subtitle="Hvad er EMS?" className="mb-10">
              Elektrisk stimulation. <br />
              <span className="text-[#9A8E81] italic">Menneskelig</span> præcision.
            </Heading>
            <div className="space-y-8 text-lg leading-relaxed text-[#494540]">
              <p>
                EMS står for <strong className="font-semibold text-[#1C1B1A]">Elektrisk Muskel Stimulation</strong>. Træningen foregår i en Xbody træningsdragt, som stimulerer de store muskelgrupper, mens du arbejder med øvelser fra styrketræning, HIIT og pilates.
              </p>
              <p>
                Formatet er 1:1, så intensiteten, teknikken og pauserne bliver tilpasset din krop. Det gør træningen både effektiv og tryg — uanset om målet er styrke, genoptræning eller bedre kropskontakt.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-8 border-t border-[#F1E5DC] pt-10">
              {[
                { label: 'Aktiverer', value: '90%' },
                { label: 'Session', value: '20 min.' },
                { label: 'Format', value: '1:1' }
              ].map((item) => (
                <div key={item.label}>
                  <p className="font-serif text-4xl tracking-tighter text-[#1C1B1A]">{item.value}</p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#9A8E81]">{item.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <Button variant="primary" onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}>
                Book prøvetime
              </Button>
              <Button variant="outline" onClick={() => window.location.href = '/priser'}>
                Se priser
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <div className="overflow-hidden border-y border-[#CEBDAA]/30 bg-[#F1E5DC] py-10 text-[#1C1B1A]">
        <div className="flex animate-marquee whitespace-nowrap">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <span key={i} className="mx-8 font-serif text-4xl tracking-tighter text-[#CEBDAA] sm:text-6xl lg:text-7xl">
              EMS træning · Fysioterapi · Styrke · Mobilitet · Frederiksberg ·
            </span>
          ))}
        </div>
      </div>

      <Section className="bg-[#F1E5DC]">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
            <div>
              <Badge>Social proof</Badge>
              <h2 className="mt-6 font-serif text-4xl leading-[0.95] tracking-[-0.05em] text-[#1C1B1A] sm:text-6xl">
                Et roligt feed. Et aktivt studie.
              </h2>
            </div>
            <div className="lg:justify-self-end">
              <Button
                variant="outline"
                onClick={() => window.open('https://instagram.com/lkstudio_cph', '_blank')}
              >
                Følg @lkstudio_cph
                <Instagram className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="mt-14">
            <InstagramWidget widgetId="5212f119-e738-4178-8432-0019620eb8e6" />
          </div>
        </div>
      </Section>

      <Section className="bg-white">
        <div className="mx-auto max-w-5xl text-center">
          <Badge className="mb-8">Kom i gang</Badge>
          <h2 className="font-serif text-4xl font-normal leading-[0.95] tracking-tight text-[#1C1B1A] sm:text-6xl lg:text-7xl">
            Start med én <br />
            <span className="text-[#9A8E81] italic">prøvetime.</span>
          </h2>
          <p className="mx-auto mt-10 max-w-2xl text-xl leading-relaxed text-[#494540]">
            Mærk hvordan EMS føles, få instruktion i dragten og tal med os om dine mål, skader eller træningserfaring.
          </p>
          <div className="mt-12 flex justify-center">
            <Button variant="primary" onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}>
              Book din prøvetime
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
